import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PaystackButton } from 'react-paystack';
import config from '../config/config';

const Subscription = ({ subsType, user_name, walletBalance }) => {
  const token = localStorage.getItem('token');
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reason, setReason] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [notification, setNotification] = useState(null);
  const [userData, setUserData] = useState({
    user_id: '',
    name: '',
    user_name: '',
    email: '',
    subs_type: '',
    is_subscribed: false,
    profile_picture: '', // Assuming this field contains the profile picture URL
    subscription_details: null,
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/user-details/${user_name}`, { headers });
      const { user, user_details, subscriptions } = response.data.data;
      setUserData({
        user_id: user.id,
        name: user.name,
        user_name: user.user_name,
        email: user.email,
        subs_type: user.subs_type,
        is_subscribed: user.is_subscribed,
        profile_picture: user_details.user_profile,
        subscription_details: user.subscription_details,
      });
      setSubscriptions(subscriptions || []);
    } catch (error) {
      showToast('Failed to fetch user details. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [user_name]);

  const showToast = (message, type) => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => {
      setToastMessage('');
      setToastType('');
    }, 5000);
  };

  // Utility function to check if the profile picture URL is valid
  const isValidImageUrl = (url) => {
    return (
      url &&
      url.startsWith('http') &&
      (url.endsWith('.jpg') || url.endsWith('.jpeg') || url.endsWith('.png') || url.endsWith('.gif'))
    );
  };

  const triggerSubscriptionNotification = (name, userName, profilePic, apiMessage) => {
    setToastMessage('');
    setToastType('');

    // Determine whether to use the profile picture or initials
    const renderProfilePic = isValidImageUrl(profilePic)
      ? profilePic
      : name.split(' ').map(word => word.charAt(0).toUpperCase()).join('');

    setNotification({
      name,
      userName,
      profilePic: renderProfilePic,
      message: apiMessage || `${name} subscribed to your profile!`,
    });

    setTimeout(() => {
      setNotification(null);
    }, 40000);
  };

  const handleSubscribe = async (plan = null, paymentMethod = '', transaction_id = '') => {
    if (!userData.user_id) {
      showToast('User ID is required for subscription.', 'error');
      return;
    }

    const requestData = {
      subscribe_to_user_id: userData.user_id,
      user_plan_id: plan ? plan.plan_id : null,
      payment_method: paymentMethod,
      transaction_id: paymentMethod === 'userwallet' ? '' : transaction_id, // Send empty string for wallet payments
    };

    // Check if payment method is 'userwallet'
    if (paymentMethod === 'userwallet') {
      const walletBalance = userData.wallet_balance;
      const planAmount = plan.plan_amount;

      // Ensure wallet balance is sufficient
      if (walletBalance < planAmount) {
        showToast('Insufficient wallet balance for this subscription.', 'error');
        return;
      }
    }

    try {
      const response = await axios.post(`${config.baseURL}/subscribe-user`, requestData, { headers });
      if (response.data.success) {
        setSelectedSubscription(null);
        showToast(response.data.message || 'Subscription successful!', 'success');
        fetchUserDetails();
        setTimeout(function() {
          window.location.reload();
        }, 5000); // 2000 milliseconds = 2 seconds
 // window.location.reload();        
        triggerSubscriptionNotification(userData.name, userData.user_name, userData.profile_picture, response.data.message);
      } else {
        showToast(response.data.error || 'Subscription failed', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Error subscribing user', 'error');
    }
  };

  const handleUnsubscribe = async () => {
    const payload = {
      subscription_id: userData.subscription_details?.subscription_id,
      unsubscribe_reason: reason,
    };

    try {
      const response = await axios.post(`${config.baseURL}/unsubscribe-user`, payload, { headers });
      if (response.data.success) {
        setShowUnsubscribeModal(false);
        showToast(response.data.message || 'Unsubscription successful!', 'success');
        fetchUserDetails();
         window.location.reload();
      } else {
        showToast(response.data.message || 'Unsubscription failed', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Error unsubscribing user', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowUnsubscribeModal(false);
    setShowPaymentModal(false);
  };

  const onSuccess = (reference) => {
    handleSubscribe(selectedSubscription, 'paystack', reference.reference);
    setShowPaymentModal(false);
  };

  const handleNowPayments = async () => {
    const requestData = {
      price_amount: selectedSubscription.plan_amount,
      price_currency: 'usd',
      pay_currency: 'btc',
      ipn_callback_url: 'https://yourwebsite.com/callback',
      order_id: `${userData.user_id}-${selectedSubscription.plan_id}`,
      order_description: `Subscription for ${selectedSubscription.plan_period} days`,
    };

    try {
      const response = await axios.post(
        'https://api-sandbox.nowpayments.io/v1/payment',
        requestData,
        {
          headers: {
            'x-api-key': '11H5KAD-1NC4A54-NG25HG0-Y78N8BS',
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.payment_id) {
        const { payment_id, payment_url } = response.data;
        window.open(payment_url, '_blank');
        handleSubscribe(selectedSubscription, 'nowpayment', payment_id);
        setShowPaymentModal(false);
      } else {
        showToast('Failed to initiate NowPayments transaction.', 'error');
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Error initiating NowPayments transaction', 'error');
    }
  };

  const paystackConfig = {
    reference: (new Date()).getTime().toString(),
    email: userData.email,
    amount: selectedSubscription ? selectedSubscription.plan_amount * 100 : 0, // Amount in kobo
    publicKey: 'pk_test_6d954b11ef91c1d7a8911f46a866de5f37a5771a',
    onSuccess,
  };

  const isSubscribedFree = userData.subs_type === 'free' && userData.is_subscribed;
  const subscribedPaidPlan = userData.subscription_details && userData.subscription_details.subscription_type !== 'free';

  return (
    <>
      {subsType === 'paid' && (
        <div className="subscription_content">
          {userData.is_subscribed ? (
            <div className="subscription_details">
              <Link className="subscription_btn subscribed_btn" onClick={() => setShowUnsubscribeModal(true)}>
                <span>Subscribed</span>
                <span>{userData.subscription_details.subscription_plan_period} Days ${Math.floor(userData.subscription_details.subscription_amount)} Total</span>
              </Link>
            </div>
          ) : (
            subscriptions.length > 0 &&
            subscriptions.map((subscription, index) => (
              <Link key={index} className="subscription_btn" onClick={() => {
                setSelectedSubscription(subscription);
                setShowPaymentModal(true);
              }}>
                <span>{subscription.plan_period} Days</span>
                <span>${Math.floor(subscription.plan_amount)} Total</span>
              </Link>
            ))
          )}
        </div>
      )}

      {subsType === 'free' && (
        <div className="subscription_content">
          {isSubscribedFree ? (
            <Link className="subscription_btn subscribed_btn" onClick={() => setShowUnsubscribeModal(true)}>
              <span>Subscribed</span>
              <span>for free</span>
            </Link>
          ) : (
            <Link className="subscription_btn" onClick={() => handleSubscribe()}>
              <span>Subscribe</span>
              <span>for free</span>
            </Link>
          )}
        </div>
      )}

      {toastMessage && (
        <div className={`toastmessage ${toastType} ${toastMessage ? 'show' : ''}`}>
          {toastMessage}
        </div>
      )}

      {/* Subscription Notification */}
      {notification && (
        <div className="notification">
          {isValidImageUrl(notification.profilePic) ? (
            <img src={notification.profilePic} alt="Profile Picture" className="profile-pic" />
          ) : (
            <div className="profile-initials">
              {notification.profilePic}
            </div>
          )}
          <div className="user-info">
            <div className="dfse">
              <div className="name">{notification.name}</div>
              <div className="username">@{notification.userName}</div>
            </div>
            <div className="subscription-text">{notification.message}</div>
          </div>
          <div className="close-btn" onClick={() => setNotification(null)}>×</div>
        </div>
      )}

      {/* Unsubscribe Modal */}
      <div className={`modal-overlay ${showUnsubscribeModal ? 'show' : ''}`} onClick={handleCloseModal} />
      <div className={`modal fade ${showUnsubscribeModal ? 'show' : ''}`} style={{ display: showUnsubscribeModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Unsubscribe</h5>
              <button type="button" className="btn-close" onClick={handleCloseModal} />
            </div>
            <div className="modal-body">
              <p>Are you sure you want to unsubscribe?</p>
              <select className="form-control" value={reason} onChange={(e) => setReason(e.target.value)}>
                <option value="No specific reason">No specific reason</option>
                <option value="Low posting frequency">Low posting frequency</option>
                <option value="Private messages not replied">Private messages not replied</option>
                <option value="No longer want to be subscribed">No longer want to be subscribed</option>
              </select>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary send_btn" onClick={handleCloseModal}>Close</button>
              <button type="button" className="btn btn-primary send_btn" onClick={handleUnsubscribe}>Unsubscribe</button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <div className={`modal-overlay ${showPaymentModal ? 'show' : ''}`} onClick={handleCloseModal} />
      <div className={`modal fade ${showPaymentModal ? 'show' : ''}`} style={{ display: showPaymentModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Select Payment Method</h5>
              <button type="button" className="btn-close" onClick={handleCloseModal} />
            </div>
            <div className="modal-body">
              <p>Select your preferred payment method to proceed with the subscription.</p>
              <select className="form-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="">Select Payment Method</option>
                <option value="paystack">Paystack</option>
                <option value="nowpayments">NowPayments</option>
                {walletBalance > 0 && (
                  <option value="userwallet">Wallet Balance (${walletBalance})</option>
                )}
              </select>
            </div>
            <div className="modal-footer">
              {paymentMethod === 'paystack' && (
                <PaystackButton className="btn btn-primary send_btn" {...paystackConfig}>Pay Now</PaystackButton>
              )}
              {paymentMethod === 'nowpayments' && (
                <button type="button" className="btn btn-primary send_btn" onClick={handleNowPayments}>Pay Now</button>
              )}
              {paymentMethod === 'userwallet' && (
                <button type="button" className="btn btn-primary send_btn" onClick={() => handleSubscribe(selectedSubscription, 'userwallet')}>Pay with Wallet</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Subscription;
